var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useCallback, useLayoutEffect, useRef, useState, } from "react";
import { useIntl } from "react-intl";
import { resizeColumnWeights, resolveResizeBoundaries, } from "../utils/columnPrefs";
import css from "./ColumnResizeOverlay.module.css";
var measureGeometry = function (container, visibleColumnIds, weights) {
    var headers = Array.from(container.querySelectorAll('[role="columnheader"]'));
    if (headers.length === 0 || headers.length !== visibleColumnIds.length) {
        return null;
    }
    var containerRect = container.getBoundingClientRect();
    var firstRect = headers[0].getBoundingClientRect();
    var rightEdgeOf = new Map(visibleColumnIds.map(function (id, index) { return [
        id,
        headers[index].getBoundingClientRect().right - containerRect.left,
    ]; }));
    var boundaries = resolveResizeBoundaries(visibleColumnIds, weights).map(function (boundary) {
        var _a;
        return (__assign(__assign({}, boundary), { x: (_a = rightEdgeOf.get(boundary.leftId)) !== null && _a !== void 0 ? _a : 0 }));
    });
    return {
        top: firstRect.top - containerRect.top,
        height: firstRect.height,
        guideHeight: Math.max(firstRect.height, containerRect.bottom - firstRect.top),
        boundaries: boundaries,
    };
};
export var ColumnResizeOverlay = function (_a) {
    var containerRef = _a.containerRef, visibleColumnIds = _a.visibleColumnIds, weights = _a.weights, percentBase = _a.percentBase, onChange = _a.onChange, onCancel = _a.onCancel;
    var formatMessage = useIntl().formatMessage;
    var _b = useState(null), geometry = _b[0], setGeometry = _b[1];
    var dragRef = useRef(null);
    var _c = useState(null), activeBoundary = _c[0], setActiveBoundary = _c[1];
    var _d = useState(null), guideX = _d[0], setGuideX = _d[1];
    var _e = useState(null), dragWeights = _e[0], setDragWeights = _e[1];
    useLayoutEffect(function () {
        var frame = 0;
        var measure = function () {
            var container = containerRef.current;
            if (container && !dragRef.current) {
                setGeometry(measureGeometry(container, visibleColumnIds, weights));
            }
        };
        // MCL converts the new widths to pixels one render later, so measure after it settles
        frame = requestAnimationFrame(function () {
            frame = requestAnimationFrame(measure);
        });
        return function () { return cancelAnimationFrame(frame); };
    }, [containerRef, visibleColumnIds, weights, percentBase]);
    var handlePointerDown = useCallback(function (boundary) { return function (event) {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        dragRef.current = {
            leftId: boundary.leftId,
            startX: event.clientX,
            startLeft: boundary.x,
            startWeights: weights,
        };
        setActiveBoundary(boundary.leftId);
        setGuideX(boundary.x);
    }; }, [weights]);
    var shiftedWeights = function (event) {
        var drag = dragRef.current;
        if (!drag || percentBase <= 0) {
            return null;
        }
        var deltaPercent = ((event.clientX - drag.startX) / percentBase) * 100;
        return resizeColumnWeights(drag.startWeights, visibleColumnIds, drag.leftId, deltaPercent);
    };
    /**
     * While dragging, handle positions come from the weights instead of the measured DOM: the
     * table does resize live, but a re-measure lands two frames after MCL applies the widths,
     * so reading positions back made the handle trail the cursor and snap at the end.
     * Deriving them from the weights (never straight from event.clientX) is also what keeps the
     * handle on the real boundary once the columns on the right hit their minimum: the pointer
     * keeps going, the weights do not, and the handle stays where the column edge actually lands.
     */
    var shiftSince = function (drag, upToId) {
        var applied = dragWeights;
        if (!applied) {
            return 0;
        }
        var moved = 0;
        for (var _i = 0, visibleColumnIds_1 = visibleColumnIds; _i < visibleColumnIds_1.length; _i++) {
            var id = visibleColumnIds_1[_i];
            if (applied[id] !== undefined) {
                moved += applied[id] - drag.startWeights[id];
            }
            if (id === upToId) {
                break;
            }
        }
        return (moved / 100) * percentBase;
    };
    var boundaryPosition = function (boundary) {
        var drag = dragRef.current;
        if (!drag) {
            return boundary.x;
        }
        return boundary.x + shiftSince(drag, boundary.leftId);
    };
    var handlePointerMove = function (event) {
        var drag = dragRef.current;
        if (!drag || event.clientX === drag.startX) {
            return;
        }
        var next = shiftedWeights(event);
        if (!next) {
            return;
        }
        setDragWeights(next);
        setGuideX(drag.startLeft +
            ((next[drag.leftId] - drag.startWeights[drag.leftId]) / 100) *
                percentBase);
        onChange(next, false);
    };
    var handlePointerUp = function (event) {
        var drag = dragRef.current;
        var next = shiftedWeights(event);
        dragRef.current = null;
        setActiveBoundary(null);
        setGuideX(null);
        setDragWeights(null);
        // a plain click (no movement) must not persist weights and mark the layout customized
        if (!drag || event.clientX === drag.startX) {
            onCancel();
            return;
        }
        if (next) {
            onChange(next, true);
        }
    };
    if (!geometry) {
        return null;
    }
    return (React.createElement("div", { className: css.overlay, style: { top: geometry.top, height: geometry.height }, "data-test-column-resize-overlay": true },
        guideX !== null && (React.createElement("div", { className: css.guide, style: { left: guideX, height: geometry.guideHeight }, "data-test-column-resize-guide": true })),
        geometry.boundaries.map(function (boundary) { return (React.createElement("div", { key: boundary.leftId, role: "separator", "aria-orientation": "vertical", "aria-label": formatMessage({
                id: "ui-br-legaldeposits.columns.resizeHandle",
            }), "data-test-column-resize-handle": boundary.leftId, className: activeBoundary === boundary.leftId
                ? "".concat(css.handle, " ").concat(css.handleActive)
                : css.handle, style: {
                left: activeBoundary === boundary.leftId && guideX !== null
                    ? guideX
                    : boundaryPosition(boundary),
            }, onPointerDown: handlePointerDown(boundary), onPointerMove: handlePointerMove, onPointerUp: handlePointerUp, onPointerCancel: handlePointerUp })); })));
};
